"use client";

import { Suspense, use } from "react";
import {
  Clock, Code2, Gem, Icon, Instagram, Linkedin, Mail, Video, Youtube,
  type IconNode, type LucideIcon, type LucideProps,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";

interface DynamicIconProps {
  icon: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Icons shipped with the page: the footer's social links, which render on
 * almost every route, and the fallback. This list is only a shortcut. Any
 * other Lucide name the CMS holds still renders, fetched on first use; a
 * namespace import of lucide-react put all 1,500 icons in the root layout.
 */
const BUNDLED: Record<string, LucideIcon> = { Clock, Code2, Gem, Instagram, Linkedin, Mail, Video, Youtube };

// The CMS stores "ShieldCheck", Lucide's import map is keyed "shield-check".
const key = (name: string) => name.replace(/[^a-z0-9]/gi, "").toLowerCase();

let lucide: Promise<Map<string, () => Promise<{ __iconNode: IconNode }>>> | undefined;
const fetched = new Map<string, Promise<IconNode | null>>();

/** The drawing for a Lucide name, or null when Lucide has no such icon. One promise per name, as use() needs. */
function fetchIcon(icon: string) {
  let node = fetched.get(icon);
  if (!node) {
    lucide ??= import("lucide-react/dynamicIconImports").then(
      ({ default: imports }) => new Map(Object.entries(imports).map(([name, load]) => [key(name), load])),
    );
    node = lucide
      .then((imports) => {
        const name = key(icon);
        // lucide-react also exports every icon as LucideName and NameIcon.
        const load = imports.get(name) ?? imports.get(name.replace(/^lucide|icon$/g, ""));
        return load ? load().then((module) => module.__iconNode) : null;
      })
      .catch(() => {
        // A chunk that failed to download must not take the page down; the next mount tries again.
        lucide = undefined;
        fetched.delete(icon);
        return null;
      });
    fetched.set(icon, node);
  }
  return node;
}

function FetchedIcon({ icon, ...props }: LucideProps & { icon: string }) {
  const node = use(fetchIcon(icon));
  return node ? <Icon iconNode={node} {...props} /> : <Code2 {...props} />;
}

export function DynamicIcon({ icon, className, style }: DynamicIconProps) {
  // SVG string kontrolü
  if (icon.startsWith("<svg")) {
    return (
      <span
        className={cn("icon-svg inline-flex items-center justify-center", className)}
        style={{ width: '1em', height: '1em', ...style }}
        dangerouslySetInnerHTML={{ __html: icon }}
      />
    );
  }

  // Lucide icon kontrolü
  const Bundled = Object.prototype.hasOwnProperty.call(BUNDLED, icon) ? BUNDLED[icon] : null;
  if (Bundled) return <Bundled className={cn(className)} style={style} strokeWidth={2} />;

  return (
    // The placeholder keeps the icon's box, so nothing moves when it arrives.
    <Suspense fallback={<span className={cn("inline-block", className)} style={style} aria-hidden />}>
      <FetchedIcon icon={icon} className={cn(className)} style={style} strokeWidth={2} />
    </Suspense>
  );
}
